/**
 * React Query v5 hooks for B1.10 DayCloseWizard.
 *
 * The feature layer exposes one query (getSnapshot) and five mutations
 * (start / patchCash / patchNonCash / patchDeposit / submit / reopen)
 * mirroring the ADR-110 state machine transitions.
 *
 * Cache key family: `['day-close', sessionId]`. Mutations use
 * optimistic setQueryData, with rollback on error.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
  type UseMutationOptions,
} from '@tanstack/react-query';

import {
  getDayCloseSnapshot,
  patchCashCounts,
  patchDeposit,
  patchNonCashCounts,
  reopenDayClose,
  startDayClose,
  submitDayClose,
} from './day-close-api';
import type {
  DayCloseSnapshot,
  PatchCashCountsRequest,
  PatchDepositRequest,
  PatchNonCashCountsRequest,
  ReopenDayCloseRequest,
  StartDayCloseRequest,
  SubmitDayCloseRequest,
} from './types';

export function dayCloseKey(sessionId: string | undefined): QueryKey {
  return ['day-close', sessionId ?? 'pending'];
}

export function useDayCloseSnapshot(sessionId: string | undefined) {
  return useQuery({
    queryKey: dayCloseKey(sessionId),
    queryFn: () => getDayCloseSnapshot(sessionId ?? ''),
    enabled: Boolean(sessionId),
    staleTime: 30_000,
  });
}

function useSnapshotMutation<TRequest>(
  sessionId: string | undefined,
  fn: (req: TRequest) => Promise<DayCloseSnapshot>,
  options?: UseMutationOptions<DayCloseSnapshot, Error, TRequest>,
) {
  const qc = useQueryClient();
  return useMutation<DayCloseSnapshot, Error, TRequest>({
    mutationFn: fn,
    onSuccess: (snapshot, ...rest) => {
      qc.setQueryData(dayCloseKey(sessionId), snapshot);
      options?.onSuccess?.(snapshot, ...rest);
    },
    ...options,
  });
}

export function useStartDayClose(
  options?: UseMutationOptions<DayCloseSnapshot, Error, StartDayCloseRequest>,
) {
  const qc = useQueryClient();
  return useMutation<DayCloseSnapshot, Error, StartDayCloseRequest>({
    mutationFn: startDayClose,
    onSuccess: (snapshot, ...rest) => {
      qc.setQueryData(dayCloseKey(snapshot.sessionId), snapshot);
      options?.onSuccess?.(snapshot, ...rest);
    },
    ...options,
  });
}

export function usePatchCashCounts(sessionId: string | undefined) {
  return useSnapshotMutation<PatchCashCountsRequest>(sessionId, patchCashCounts);
}

export function usePatchNonCashCounts(sessionId: string | undefined) {
  return useSnapshotMutation<PatchNonCashCountsRequest>(
    sessionId,
    patchNonCashCounts,
  );
}

export function usePatchDeposit(sessionId: string | undefined) {
  return useSnapshotMutation<PatchDepositRequest>(sessionId, patchDeposit);
}

export function useSubmitDayClose(sessionId: string | undefined) {
  return useSnapshotMutation<SubmitDayCloseRequest>(sessionId, submitDayClose);
}

export function useReopenDayClose(sessionId: string | undefined) {
  return useSnapshotMutation<ReopenDayCloseRequest>(sessionId, reopenDayClose);
}
